import type { GenericHttpResponse } from '@iconik-io/core/src/http/generic_http_transport';

import type { EndpointsMap } from '../endpoints';
import type { HttpHeaders } from '../types';
import type { EndpointRequest } from './request';

export type EndpointResponse<
  Request extends EndpointRequest = EndpointRequest,
  Meta extends object = Record<string, unknown>,
> = {
  status: number;
  statusText?: string;
  headers: HttpHeaders;
  body: EndpointsMap[Request['id']]['responseBody'];
  request: Request;
  meta?: Meta;
};

export type AnyEndpointResponse = EndpointResponse<any>;

export class EndpointError<
  Request extends EndpointRequest = EndpointRequest,
> extends Error {
  request: Request;

  response: AnyEndpointResponse;

  cause: Error | undefined;

  constructor(request: Request, response: AnyEndpointResponse, cause?: Error) {
    const { statusText, status } = response;
    let message = `HTTP Request failed with status code ${status}`;
    if (statusText) {
      message += `: ${statusText}`;
    }
    super(message);
    this.request = request;
    this.response = response;
    this.cause = cause;
  }
}

export class AbortEndpointRequestError extends Error {}

export const toEndpointResponse = <Request extends EndpointRequest>(
  request: Request,
  response: GenericHttpResponse,
): EndpointResponse<Request> => ({
  status: response.status,
  statusText: response.statusText,
  headers: response.headers,
  // biome-ignore lint/plugin: the transport can't know the endpoint's body type, this is the one boundary where it is assumed.
  body: response.body as EndpointResponse<Request>['body'],
  request,
});
